'use client';

import { useLayoutEffect, useState, useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { Text, Icon, useUi, Link } from '@weareconceptstudio/core';
import { useCart } from '@weareconceptstudio/cart';

import { withDataContext, withUIContext } from '@/context';
import { config } from '@/helpers';
import lottie from '@/lottieFiles/shop.json';

import BannerHeader from './Banner';
import FulfillmentChips from './FulfillmentChips';
import ShopFulfillmentBar from './ShopFulfillmentBar';
import { useMobileHeaderScroll } from './useMobileHeaderScroll';
import LottieAnimation from '../LottieAnimation';
import LanguageSwitcher from '../LanguageSwitcher';
import Logo from '../Logo';

import HeaderStyle from './style';
import { HEADER_COLLAPSIBLE_MAX_WIDTH, LOCALES } from '@/constants';
const HOME_DESKTOP_DELIVERY_EVENT = 'home-desktop-delivery-past';

const Header = ({ text, phone, loadAnimation, globalData }) => {
	const pathname = usePathname();
	const { itemsCount } = useCart();
	const { winWidth } = useUi();
	const effectiveWidth = typeof window !== 'undefined' ? window.innerWidth : winWidth;
	const pathSegments = pathname.split('/').filter(Boolean);
	const normalizedSegments = LOCALES.includes(pathSegments[0]) ? pathSegments.slice(1) : pathSegments;
	const isHomePage = pathSegments.length === 0 || (pathSegments.length === 1 && LOCALES.includes(pathSegments[0]));
	const isShopPage = normalizedSegments[0] === 'menu';
	const isCollapsibleViewport = effectiveWidth <= HEADER_COLLAPSIBLE_MAX_WIDTH;
	const hasHomeMobileHeader = isCollapsibleViewport && isHomePage;
	const hasShopCollapsibleHeader = isCollapsibleViewport && isShopPage;
	const hasMobileCollapsibleHeader = hasHomeMobileHeader || hasShopCollapsibleHeader;
	const isCondensedHeader = !isHomePage && effectiveWidth > 768 && effectiveWidth <= HEADER_COLLAPSIBLE_MAX_WIDTH;
	const isHomeDesktopHeader = isHomePage && !hasMobileCollapsibleHeader;
	const hasDesktopCenterFulfillment = !hasMobileCollapsibleHeader && (isHomePage || isShopPage);
	const [animLottie, setAnimLottie] = useState(false);
	const [isHomeDesktopDeliveryPast, setIsHomeDesktopDeliveryPast] = useState(false);
	const desktopHeaderRef = useRef(null);
	const rightBlockRef = useRef(null);

	useEffect(() => {
		if (!isHomeDesktopHeader) {
			setIsHomeDesktopDeliveryPast(false);
			return undefined;
		}

		const onDeliveryPast = (event) => {
			setIsHomeDesktopDeliveryPast(Boolean(event.detail?.active));
		};

		window.addEventListener(HOME_DESKTOP_DELIVERY_EVENT, onDeliveryPast);

		return () => {
			window.removeEventListener(HOME_DESKTOP_DELIVERY_EVENT, onDeliveryPast);
			setIsHomeDesktopDeliveryPast(false);
		};
	}, [isHomeDesktopHeader, pathname]);

	const shouldShowShopOnlineText = isHomePage || isCollapsibleViewport;
	const shouldShowCartCount = isHomePage || isCollapsibleViewport;

	const { headerRef, navSlotRef, navRef, fulfillmentRef, collapseLayerRef } = useMobileHeaderScroll(
		hasMobileCollapsibleHeader,
		{ isShop: hasShopCollapsibleHeader }
	);
	useLayoutEffect(() => {
		document.body.classList.toggle('has-mobile-header-layout', hasMobileCollapsibleHeader);
		return () => document.body.classList.remove('has-mobile-header-layout');
	}, [hasMobileCollapsibleHeader]);

	useLayoutEffect(() => {
		document.body.classList.toggle('has-shop-fulfillment-bar', hasShopCollapsibleHeader);
		return () => document.body.classList.remove('has-shop-fulfillment-bar');
	}, [hasShopCollapsibleHeader]);

	useLayoutEffect(() => {
		if (hasMobileCollapsibleHeader) return undefined;

		document.body.classList.remove('is-header-nav-hidden', 'is-header-animating');
		document.body.classList.remove('has-home-desktop-sticky-delivery');
		document.documentElement.style.removeProperty('--header-total-height');
		document.documentElement.style.removeProperty('--header-collapsed-bottom');
		document.documentElement.style.removeProperty('--visible-header-bottom');
		document.documentElement.style.removeProperty('--nav-height');
		document.documentElement.style.removeProperty('--header-banner-slot-height');

		return undefined;
	}, [hasMobileCollapsibleHeader, pathname]);

	useLayoutEffect(() => {
		if (!hasDesktopCenterFulfillment) return undefined;

		const header = desktopHeaderRef.current;
		const rightBlock = rightBlockRef.current;
		if (!header || !rightBlock) return undefined;

		const syncRightBlockWidth = () => {
			header.style.setProperty('--header-right-block-width', `${rightBlock.offsetWidth}px`);
		};

		syncRightBlockWidth();

		const resizeObserver = new ResizeObserver(syncRightBlockWidth);
		resizeObserver.observe(rightBlock);
		window.addEventListener('resize', syncRightBlockWidth);

		return () => {
			resizeObserver.disconnect();
			window.removeEventListener('resize', syncRightBlockWidth);
			header.style.removeProperty('--header-right-block-width');
		};
	}, [hasDesktopCenterFulfillment, effectiveWidth, itemsCount, shouldShowShopOnlineText]);

	if (hasMobileCollapsibleHeader) {
		const headerClassName = [
			isCondensedHeader ? 'is-condensed-header' : '',
			'has-mobile-collapsible-header',
			hasHomeMobileHeader ? 'is-home-mobile-header' : '',
			hasShopCollapsibleHeader ? 'has-shop-fulfillment-bar' : '',
		]
			.filter(Boolean)
			.join(' ');

		return (
			<HeaderStyle
				ref={headerRef}
				className={headerClassName}>
				<div className='header-top-scroll'>
					<div className='header-banner-slot'>
						<BannerHeader
							text={text}
							phone={phone}
							loadAnimation={loadAnimation}
							isMobileBanner
						/>
					</div>

					<div
						className='header-collapse-layer'
						ref={collapseLayerRef}>
						<div
							className='header-nav-slot'
							ref={navSlotRef}>
							<div className='header-nav-slot-inner'>
								<div
									className='header-container opacity-zero'
									ref={navRef}>
									<Logo
										className='logo-block cursor-pointer'
										useMobileLogo
									/>

									<div className='right-block'>
										<Link
											url={config.routes.menu.path}
											className={'shop-online-link-block'}>
											<div
												className='shop-online-block'
												onMouseEnter={() => setAnimLottie(true)}
												onMouseLeave={() => setAnimLottie(false)}>
												{effectiveWidth >= 1280 ? (
													<LottieAnimation
														loop
														autoplay={false}
														animData={lottie}
														isPlay={animLottie}
														onCompletePause={!animLottie}
													/>
												) : (
													<LottieAnimation
														isPaused
														loop={false}
														isPlay={false}
														autoplay={false}
														animData={lottie}
														onCompletePause={false}
													/>
												)}

												{shouldShowShopOnlineText && (
													<Text
														className={`p p4`}
														text={'shopOnline'}
													/>
												)}
											</div>
										</Link>

										<LanguageSwitcher />

										<Link url={config.routes.account.path}>
											<Icon
												name={'login'}
												className={'login-icon'}
											/>
										</Link>

										<Link url={config.routes.cart.path}>
											<div className='cart-block'>
												<Icon
													name={'shop'}
													className={'shop-icon'}
												/>

												{shouldShowCartCount && (
													<div className='ext-block'>
														(<Text className={'p p3 align-center'}>{`${itemsCount}`}</Text>)
													</div>
												)}
											</div>
										</Link>
									</div>
								</div>
							</div>
						</div>

						{hasShopCollapsibleHeader && (
							<div
								className='shop-fulfillment-slot'
								ref={fulfillmentRef}>
								<ShopFulfillmentBar globalData={globalData} />
							</div>
						)}
					</div>
				</div>
			</HeaderStyle>
		);
	}

	return (
		<HeaderStyle
			ref={desktopHeaderRef}
			className={[
				isCondensedHeader ? 'is-condensed-header' : '',
				hasDesktopCenterFulfillment ? 'has-desktop-center-fulfillment' : '',
				isHomeDesktopHeader ? 'is-home-desktop-header' : '',
				isHomeDesktopDeliveryPast ? 'has-home-desktop-sticky-delivery' : '',
			]
				.filter(Boolean)
				.join(' ')}>
			<BannerHeader
				text={text}
				phone={phone}
				loadAnimation={loadAnimation}
			/>

			<div className='header-container opacity-zero'>
				<Logo
					className='logo-block cursor-pointer'
					useMobileLogo
				/>

				{hasDesktopCenterFulfillment && (
					<div className='header-center-fulfillment'>
						<FulfillmentChips
							allowOnHomePage={isHomeDesktopHeader}
							promptOnShopEnter={isShopPage}
							compact={false}
							iconOnly={false}
						/>
					</div>
				)}

				<div
					className='right-block'
					ref={rightBlockRef}>
					<Link
						url={config.routes.menu.path}
						className='shop-online-link-block'>
						<div
							className='shop-online-block'
							onMouseEnter={() => setAnimLottie(true)}
							onMouseLeave={() => setAnimLottie(false)}>
							{effectiveWidth >= 1280 ? (
								<LottieAnimation
									loop
									autoplay={false}
									animData={lottie}
									isPlay={animLottie}
									onCompletePause={!animLottie}
								/>
							) : (
								<LottieAnimation
									isPaused
									loop={false}
									isPlay={false}
									autoplay={false}
									animData={lottie}
									onCompletePause={false}
								/>
							)}

							<Text
								className='p p4'
								text='shopOnline'
							/>
						</div>
					</Link>

					<LanguageSwitcher />

					<Link url={config.routes.account.path}>
						<Icon
							name='login'
							className='login-icon'
						/>
					</Link>

					<Link url={config.routes.cart.path}>
						<div className='cart-block'>
							<Icon
								name='shop'
								className='shop-icon'
							/>

							<div className='ext-block'>
								(<Text className='p p3 align-center'>{`${itemsCount}`}</Text>)
							</div>
						</div>
					</Link>
				</div>
			</div>
		</HeaderStyle>
	);
};

export default withDataContext(withUIContext(Header, ['loadAnimation']), ['globalData']);
